import React, {ChangeEvent, ChangeEventHandler, FocusEventHandler, KeyboardEventHandler, useEffect, useRef, useState} from 'react';
import './DurationEntry.less';
import * as DateUtil from '../../lib/dateutils';
import Alert from '../../lib/alert';
import {formatDuration} from "../../lib/picos/duration";
import {isTouchDevice} from "../../lib/picos/browserFns";

type DurationEntryState = {
	hours: string,
	seconds: string,
	minutes: string,
	value: number,
	focus: 'hours' | 'minutes' | 'seconds' | '',
	focusOn: 'hour' | 'minute' | 'second' | '',
}

/**
 * Update the hour, minute and second fields using the input in props,
 * which contains value as the duration in milliseconds.
 */
const updateFields = (props: DurationEntryProps): {value: number, hours: string, minutes: string, seconds: string} => {
	const val = props.value < 0 ? -1 * props.value : props.value;

	if (!val) {
		return {value: 0, hours: '0', minutes: '00', seconds: '00'};
	}

	// Get the HH, MM and SS from an HH:MM:SS
	const parts = formatDuration(val, 'hh:mm:ss').split(':');

	let hours = parts[0];
	let minutes = parts[1];
	let seconds = parts[2];

	return {value: val, hours, minutes, seconds};
};

/**
 * Component to enter HH:MM:SS.
 *
 * Also handles entering HH as a decimal, e.g., 1.5 = 01:30:00
 */
const DurationEntry: React.FC<DurationEntryProps> = (props) => {
	const [state, setState] = useState<DurationEntryState>(() => ({
		...updateFields(props),
		focus: '',
		focusOn: ''
	}));

	const setPartialState = (partial: Partial<DurationEntryState>) =>
		setState(prev => ({...prev, ...partial}));

	// Ref fields for hours, minutes and seconds. Used to set focus on the appropriate field.
	const hoursRef = useRef<HTMLInputElement>(null);
	const minutesRef = useRef<HTMLInputElement>(null);
	const secondsRef = useRef<HTMLInputElement>(null);

	/**
	 * Mirror getDerivedStateFromProps: if external value changes, refresh fields from props.
	 */
	if (props.value !== state.value) {
		const next = updateFields(props);
		setState(prev => ({...prev, ...next}));
	}

	/**
	 * Check the focusOn field and make sure the correct field gets focus.
	 * (Replaces componentDidUpdate.)
	 */
	useEffect(() => {
		switch (state.focusOn) {
			case "hour":
				hoursRef.current?.select();
				break;
			case "minute":
				minutesRef.current?.select();
				break;
			case "second":
				secondsRef.current?.select();
				break;
			default:
				break;
		}

		if (state.focus !== '' && state.focusOn !== '') setPartialState({focusOn: ''});
	});

	/**
	 * Helper to convert hours/minutes/seconds to a millisecond duration and notify the parent.
	 */
	const onTimeBlur = (hours: string, minutes: string, seconds: string) => {
		let nHours = Number(hours);
		let nMinutes = Number(minutes);
		let nSeconds = Number(seconds);

		if (isNaN(nHours)) nHours = 0;
		if (isNaN(nMinutes)) nMinutes = 0;
		if (isNaN(nSeconds)) nSeconds = 0;

		const result = 1000 * ((nHours * 60 * 60) + (nMinutes * 60) + nSeconds);

		props.onChange && props.onChange(result);
	};

	/**
	 * Called when the hour value changes.
	 */
	const onHoursChange = (e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		e.persist();

		const value = e.target.value;

		if (value === '.' || !isNaN(Number(value))) {
			setPartialState({hours: e.target.value});
		}
		else if (value === null) {
			setPartialState({focusOn: 'hour'});
		}
	};

	const onHoursFocus: FocusEventHandler<HTMLInputElement> = (e) => {
		if (props.doAutofocus) e.target.select();
		setPartialState({focus: 'hours'});
	};

	const onHoursKeydown: KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (e.key === ':') {
			e.preventDefault();
			setPartialState({focusOn: 'minute'});
		}
	};

	/**
	 * Called when the hours field loses focus.
	 */
	const onHoursBlur = () => {
		const decimal = `${state.hours}`.includes('.');

		if (decimal) {
			const ft = Number.parseFloat(state.hours); // 1.73
			let hours = Number(Math.floor(ft).toFixed(0)); // 1
			const hoursRemainder = Number((ft - hours).toFixed(2)); // .73

			let minutes = Math.floor(60 * hoursRemainder);
			const minutesRemainder = (60 * hoursRemainder) - minutes;
			let seconds = Math.round(60 * minutesRemainder);

			if (seconds >= 60) {
				seconds = 59;
				minutes++;

				if (minutes >= 60) {
					minutes = 0;
					hours++;
				}
			}

			const sSeconds = seconds.toFixed(0);

			if (hoursRemainder) {
				const hoursStr = '' + hours;
				const minutesStr = '' + minutes;
				setPartialState({hours: hoursStr, minutes: minutesStr, seconds: sSeconds, focus: ''});
				onTimeBlur(hoursStr, minutesStr, sSeconds);
			}
			else {
				const hoursStr = `${hours}`;
				setPartialState({hours: hoursStr, focus: ''});
				onTimeBlur(hoursStr, state.minutes, state.seconds);
			}
		}
		else {
			setPartialState({focus: ''});
			onTimeBlur(state.hours, state.minutes, state.seconds);
		}
	};

	const onMinutesBlur = (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.value) {
			const minutes = e.target.value ? e.target.value : null;
			setPartialState({minutes, focus: ''});
			onTimeBlur(state.hours, minutes, state.seconds);
		}
		else {
			setPartialState({minutes: '00', focus: ''});
			onTimeBlur(state.hours, '00', state.seconds);
		}
	};

	// Update state on every keystroke. Do NOT call onMinutesBlur here —
	// committing mid-typing flips state.focus='' which makes the display
	// pad ('3' -> '03'), and the next keystroke lands in the wrong cursor
	// position. The native onBlur={onMinutesBlur} on the input commits on
	// real blur, which is the documented contract (see e2e billable.spec.ts).
	const onMinutesChange = (e: ChangeEvent<HTMLInputElement>) => {
		const minutes = e.target.value ? Number(e.target.value) : 0;

		if (!isNaN(minutes) && minutes >= 0 && minutes < 60) {
			setPartialState({minutes: e.target.value ? e.target.value : null});
		}
		else if (isNaN(minutes)) {
			setPartialState({focusOn: 'minute'});
		}
	};

	const onMinutesFocus = () => {
		setTimeout(() => minutesRef?.current?.select(), 100);
		setPartialState({focus: 'minutes'});
	};

	const onMinutesKeydown: KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (props.showSeconds) {
			if (e.key === 'ArrowUp') {
				onUp('M');
				onMinutesFocus();
			}
			else if (e.key === 'ArrowDown') {
				onDown('M');
				onMinutesFocus();
			}
			else if (e.key === ':') {
				e.preventDefault();
				setPartialState({focusOn: 'second'});
			}
		}
	};

	const onSecondsKeydown: KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (props.showSeconds) {
			if (e.key === 'ArrowUp') {
				onUp('S');
				onSecondsFocus();
			}
			else if (e.key === 'ArrowDown') {
				onDown('S');
				onSecondsFocus();
			}
		}
	};

	const onSecondsFocus = () => {
		setTimeout(() => secondsRef?.current?.select(), 100);
		setPartialState({focus: 'seconds'});
	};

	const onSecondsBlur: ChangeEventHandler<HTMLInputElement> = (e) => {
		if (e.target.value) {
			let seconds = Number.parseInt(e.target.value);
			if (isNaN(seconds)) seconds = 0;
			const secondsStr = `${seconds}`;
			setPartialState({seconds: secondsStr, focus: ''});
			onTimeBlur(state.hours, state.minutes, secondsStr);
		}
		else {
			setPartialState({seconds: '00', focus: ''});
			onTimeBlur(state.hours, state.minutes, '00');
		}
	};

	// Update state on every keystroke. Do NOT call onSecondsBlur here —
	// committing mid-typing flips state.focus='' which makes the display
	// pad ('3' -> '03'), and the next keystroke lands in the wrong cursor
	// position. The native onBlur={onSecondsBlur} on the input commits on
	// real blur, which is the documented contract (see e2e billable.spec.ts).
	const onSecondsChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		e.preventDefault();

		const seconds = Number.parseInt(e.target.value);
		if (!isNaN(seconds) && seconds >= 0 && seconds < 60) {
			setPartialState({seconds: e.target.value});
		}
		else if (isNaN(seconds)) {
			setPartialState({focusOn: 'second'});
		}
	};

	const onUp = (property: 'H' | 'M' | 'S') => {
		if (property === 'H') {
			setState(prevState => ({...prevState, hours: prevState.hours + 1}));
		}
		else if (property === 'M') {
			const minutes = Math.min(60, Number.parseInt(state.minutes) + 1);
			setPartialState({minutes: `${minutes}`});
		}
		else if (property === 'S') {
			const seconds = Math.min(60, Number.parseInt(state.seconds) + 1);
			setPartialState({seconds: `${seconds}`});
		}
		else Alert.addSimpleError(`Expected h/m/s, got ${property}`);
	};

	const onDown = (property: 'H' | 'M' | 'S') => {
		if (property === 'H') {
			const hours = Math.max(0, Number.parseFloat(state.hours) - 1);
			setPartialState({hours: `${hours}`});
		}
		else if (property === 'M') {
			const minutes = Math.max(0, Number.parseInt(state.minutes) - 1);
			setPartialState({minutes: `${minutes}`});
		}
		else if (property === 'S') {
			const seconds = Math.max(0, Number.parseInt(state.seconds) - 1);
			setPartialState({seconds: `${seconds}`});
		}
		else Alert.addSimpleError(`Expected h/m/s, got ${property}`);
	};

	// The hours that we will display in the hours entry
	let displayHours = '';

	if (state.hours !== '.' && (isNaN(Number(state.hours)) || state.hours === 'NaN')) displayHours = '0';
	else if (state.hours !== null && state.hours !== '') {
		displayHours = `${state.hours}`;
	}

	let minutes = (state.minutes !== null && !isNaN(Number(state.minutes)) && state.minutes !== 'NaN') ? `${state.minutes}` : '';

	let seconds = (state.seconds !== null && !isNaN(Number(state.seconds)) && state.seconds !== 'NaN') ? `${state.seconds}` : '';

	if (state.focus !== 'minutes') minutes = minutes.padStart(2, '0');

	if (state.focus !== 'seconds') seconds = seconds.padStart(2, '0');

	const timeComponent = (
		<>
			{/* Hours */}
			<input className='duration-entry__hours'
				   type="number"
				   min={0}
				   value={displayHours}
				   onChange={onHoursChange}
				   onFocus={onHoursFocus}
				   autoFocus={props.doAutofocus}
				   onKeyDown={onHoursKeydown}
				   onBlur={onHoursBlur}
				   ref={hoursRef}
				   data-lpignore="true"
				   inputMode='decimal'
			/>

			{/* Colon */}
			<div className='duration-entry__colon'>:</div>

			{/* Minutes */}
			<input className='duration-entry__minutes'
				   type="string"
				   value={minutes}
				   onFocus={onMinutesFocus}
				   onBlur={onMinutesBlur}
				   onChange={onMinutesChange}
				   onKeyDown={onMinutesKeydown}
				   ref={minutesRef}
				   data-lpignore="true"
			/>

			{/* (Optional) Colon if Seconds is shown */}
			{props.showSeconds &&
				<div className='duration-entry__colon'>:</div>
			}

			{/* (Optional) Seconds */}
			{props.showSeconds &&
				<input className='duration-entry__seconds'
					   type="string"
					   value={seconds}
					   onFocus={onSecondsFocus}
					   onBlur={onSecondsBlur}
					   onChange={onSecondsChange}
					   onKeyDown={onSecondsKeydown}
					   ref={secondsRef}
					   data-lpignore="true"
				/>
			}
		</>
	);

	// Special formatting on a mobile, touch-enabled device (set height)
	const isMobile = isTouchDevice() ? 'duration-entry--mobile' : '';

	// If the component is disabled, we just show the current duration in HH:MM:SS.
	if (props.disabled) {
		if (state.hours && state.minutes && (props.showSeconds ? state.seconds : true)) {
			return <>{DateUtil.formatTime({
				hours: state.hours,
				minutes: state.minutes,
				seconds: state.seconds
			})}</>;
		}
		else return null;
	}
	else return (
		<div className={`duration-entry ${isMobile}`}>
			{timeComponent}
		</div>
	);
};

export type DurationEntryProps = {
	// If disabled, we just show a text string for the time in HH:MM:SS
	disabled?: boolean,

	// Should we autofocus on the first (hour) field?
	doAutofocus?: boolean,

	// onChange handler, passes a numeric value for duration in milliseconds
	onChange?: (duration: number) => void,

	// This is the duration in milliseconds
	value?: number,

	// Show the seconds, or hide them?
	showSeconds?: boolean
};

export default DurationEntry;
